import { useTranslation } from "react-i18next";

export function TaxExample({
	rate,
	amount = 100,
	product = false,
	compact = false,
}: {
	rate: number;
	amount?: number;
	product?: boolean;
	compact?: boolean;
}) {
	const { t } = useTranslation();
	const valid =
		Number.isFinite(Number(rate)) &&
		Number(rate) >= 0 &&
		Number(rate) <= 100 &&
		Number.isFinite(amount) &&
		amount >= 0;
	const base = Math.round(amount * 100) / 100;
	const tax = Math.round(base * Number(rate)) / 100;
	return (
		<div className={`settings-example${compact ? " product-tax-summary" : ""}`} data-testid="tax-example">
			<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
				<h3 className={compact ? "sr-only" : "text-sm font-semibold"}>
					{t(product ? "paymentTaxDesign.priceBreakdown" : "settingsDesign.sampleCalculation")}
				</h3>
				<span className="text-xs text-muted-foreground">
					{t(product ? "paymentTaxDesign.beforeDiscounts" : "settingsDesign.sampleOnly")}
				</span>
			</div>
			<dl className="grid grid-cols-3 gap-3">
				{[
					[
						t(product ? "paymentTaxDesign.beforeTax" : "settingsDesign.sampleAmount"),
						valid ? base.toFixed(2) : "—",
					],
					[t("settingsDesign.taxAmount"), valid ? tax.toFixed(2) : "—"],
					[t("settingsDesign.afterTax"), valid ? (base + tax).toFixed(2) : "—"],
				].map(([label, value]) => (
					<div key={label} className="flex flex-col">
						<dt className="order-2 text-xs leading-relaxed text-muted-foreground">{label}</dt>
						<dd className="order-1 mb-1 text-lg font-semibold tabular-nums">
							<bdi>{value}</bdi>
						</dd>
					</div>
				))}
			</dl>
		</div>
	);
}

interface BarcodeConfig {
	barcodePrefixes: string;
	barcodeItemCodeLength: number;
	barcodeValueLength: number;
	barcodeValueDivisor: number;
	embeddedValueType: "weight" | "price";
	weightUnit: string;
}
export function BarcodeExample({ config, itemCode }: { config: BarcodeConfig; itemCode?: string }) {
	const { t } = useTranslation();
	const prefix = String(config.barcodePrefixes || "").split(/[,\s-]+/)[0];
	const itemLength = Number(config.barcodeItemCodeLength);
	const valueLength = Number(config.barcodeValueLength);
	const divisor = Number(config.barcodeValueDivisor);
	const valid =
		/^\d{2}$/.test(prefix) &&
		[itemLength, valueLength].every(n => Number.isInteger(n) && n > 0 && n <= 10) &&
		divisor > 0 &&
		(itemCode === undefined || (/^\d+$/.test(itemCode) && itemCode.length <= itemLength));
	const item = valid
		? itemCode === undefined
			? "1234567890".slice(0, itemLength)
			: itemCode.padStart(itemLength, "0")
		: "";
	const value = valid ? "1250".slice(-valueLength).padStart(valueLength, "0") : "";
	const digits = prefix + item + value;
	const check =
		valid && digits.length === 12
			? String((10 - (digits.split("").reduce((sum, n, i) => sum + Number(n) * (i % 2 ? 3 : 1), 0) % 10)) % 10)
			: null;
	return (
		<div className="settings-example barcode-preview" data-testid="barcode-example">
			<h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
				<span>{t("settingsDesign.barcodePreview")}</span>
				{check !== null && (
					<span className="text-xs font-normal text-muted-foreground" dir="ltr">
						EAN-13
					</span>
				)}
			</h3>
			<p className="mt-1 text-xs text-muted-foreground">
				{t("settingsDesign.sampleOnly")} · {t("settingsDesign.barcodeFormat")}
			</p>
			{valid ? (
				<>
					<div className="barcode-segments" dir="ltr">
						{[
							[prefix, t("settingsDesign.prefix")],
							[item, t("settingsDesign.itemCode")],
							[
								value,
								t(
									config.embeddedValueType === "weight"
										? "weightSalesConfig.barcode.valueWeight"
										: "weightSalesConfig.barcode.valuePrice",
								),
							],
							...(check !== null ? [[check, t("settingsDesign.checkDigit")]] : []),
						].map(([number, label], index) => (
							<div key={label} className={`barcode-segment barcode-segment-${index}`}>
								<bdi className="barcode-digits">{number}</bdi>
								<span className="text-xs text-muted-foreground">{label}</span>
							</div>
						))}
					</div>
					<p className="barcode-decoded">
						<span>
							{t("settingsDesign.scannedBarcode")}:{" "}
							<bdi className="font-semibold text-primary">
								{digits}
								{check ?? ""}
							</bdi>
						</span>
						<span>
							{t("settingsDesign.itemCode")}: <bdi className="font-semibold">{item}</bdi>
						</span>
						<span>
							<span className="text-muted-foreground">{t("settingsDesign.decodedValue")}: </span>
							<bdi className="font-semibold">
								{(Number(value) / divisor).toLocaleString("en-US", { maximumFractionDigits: 6 })}
								{config.embeddedValueType === "weight" ? ` ${config.weightUnit}` : ""}
							</bdi>
						</span>
					</p>
				</>
			) : (
				<p className="mt-4 text-sm text-muted-foreground">
					{t(itemCode !== undefined ? "productEditor.pluPreviewHint" : "settingsDesign.barcodeInvalid")}
				</p>
			)}
		</div>
	);
}
